import { Component, HostBinding, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Sort } from "@angular/material/sort";
import { Subject, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { DigitalCardService } from "../digital-card.service";
import { ToastrService } from "../../shared/toastr.service";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";
import { DigitalCardSourceCreateDialogComponent } from "../digital-card-source-create-dialog/digital-card-source-create-dialog.component";
import { DigitalCardSourceShareDialogComponent } from "../digital-card-source-share-dialog/digital-card-source-share-dialog.component";
import type {
  DigitalCardCustomSourceItem,
  DigitalCardSourceShareDialogData,
} from "../digital-cards.interface";

@Component({
  selector: "app-digital-card-custom-sources",
  templateUrl: "./digital-card-custom-sources.component.html",
  styleUrls: ["./digital-card-custom-sources.component.scss"],
  standalone: false,
})
export class DigitalCardCustomSourcesComponent implements OnInit, OnDestroy {
  readonly displayedColumns = ["name", "hits", "createdAt", "actions"];

  allSources: DigitalCardCustomSourceItem[] = [];
  filteredSources: DigitalCardCustomSourceItem[] = [];
  tableLoading = false;
  loadFailed = false;
  deletingId: string | null = null;
  sortBy: "name" | "hits" | "createdAt" = "createdAt";
  sortDirection: "asc" | "desc" = "desc";
  searchControl = new FormControl<string>("", { nonNullable: true });

  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  @HostBinding("class.is-empty")
  get isEmpty(): boolean {
    return (
      !this.tableLoading && !this.loadFailed && this.allSources.length === 0
    );
  }

  get showNoSearchResults(): boolean {
    return (
      !this.tableLoading &&
      !this.loadFailed &&
      this.allSources.length > 0 &&
      this.filteredSources.length === 0
    );
  }

  constructor(
    private digitalCardService: DigitalCardService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.reload$
      .pipe(
        switchMap(() => {
          this.tableLoading = true;
          this.loadFailed = false;
          return this.digitalCardService.listCustomSources().pipe(
            catchError(() => {
              if (this.allSources.length === 0) this.loadFailed = true;
              return of(null);
            }),
            finalize(() => {
              this.tableLoading = false;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        if (!res) return;
        this.loadFailed = false;
        this.allSources = res.sources ?? [];
        this.applyFilters();
      });

    this.searchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.reload$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  loadSources(): void {
    this.reload$.next();
  }

  private applyFilters(): void {
    const phrase = this.searchControl.value.trim().toLowerCase();
    let rows = [...this.allSources];
    if (phrase) {
      rows = rows.filter((row) => row.name.toLowerCase().includes(phrase));
    }
    rows.sort((a, b) => {
      const dir = this.sortDirection === "asc" ? 1 : -1;
      if (this.sortBy === "hits") return (a.totalHits - b.totalHits) * dir;
      if (this.sortBy === "createdAt") {
        return (
          (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) *
          dir
        );
      }
      return a.name.localeCompare(b.name) * dir;
    });
    this.filteredSources = rows;
  }

  onSortChange(sort: Sort): void {
    const active = sort.active;
    if (active === "name" || active === "hits" || active === "createdAt") {
      this.sortBy = active;
      this.sortDirection = sort.direction === "asc" ? "asc" : "desc";
      this.applyFilters();
    }
  }

  openCreate(): void {
    this.dialog
      .open(DigitalCardSourceCreateDialogComponent, {
        width: "420px",
        autoFocus: true,
        panelClass: "app-dialog-panel",
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((created: DigitalCardCustomSourceItem | null) => {
        if (!created) return;
        this.loadSources();
      });
  }

  openShare(source: DigitalCardCustomSourceItem, event?: Event): void {
    event?.stopPropagation();
    const data: DigitalCardSourceShareDialogData = {
      sourceId: source.id,
      sourceName: source.name,
      medium: source.medium,
    };
    this.dialog.open(DigitalCardSourceShareDialogComponent, {
      width: "400px",
      maxWidth: "95vw",
      panelClass: "app-dialog-panel",
      data,
    });
  }

  deleteSource(source: DigitalCardCustomSourceItem, event: Event): void {
    event.stopPropagation();
    if (this.deletingId) return;
    const data: ConfirmationDialogParams = {
      title: "digitalCards.sources.deleteConfirmTitle",
      body: this.translate.instant("digitalCards.sources.deleteConfirmBody", {
        name: source.name,
      }) as string,
      confirmButton: "digitalCards.sources.delete",
    };
    this.dialog
      .open(ConfirmationDialogComponent, { data, width: "420px" })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ok) => {
        if (!ok) return;
        this.deletingId = source.id;
        this.digitalCardService
          .deleteCustomSource(source.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.deletingId = null;
              this.loadSources();
            },
            error: () => {
              this.deletingId = null;
              this.toastr.error(
                this.translate.instant(
                  "digitalCards.sources.deleteFailed",
                ) as string,
              );
            },
          });
      });
  }

  formatCreatedAt(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
  }
}
